/**
 * CESM OSINT System - Strategic Alerts Panel
 * Système d'alertes sur mots-clés stratégiques des sources adversaires
 * 
 * Concept & Développement: CDT (R) Jean-Baptiste GIRAUD
 * LPP Logiciel © 2026
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bell, AlertTriangle, Shield, Zap, Globe, Target,
  RefreshCw, Check, CheckCheck, Trash2, ExternalLink,
  Loader2, Filter, ChevronDown, ChevronUp, X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { addGlobalLog } from './GlobalConsole';

// Configuration des priorités
const PRIORITY_CONFIG = {
  critical: { 
    label: 'CRITIQUE', 
    color: 'text-red-400',
    bgColor: 'bg-red-900/30',
    borderColor: 'border-red-500',
    icon: AlertTriangle
  },
  high: { 
    label: 'HAUTE', 
    color: 'text-orange-400',
    bgColor: 'bg-orange-900/30',
    borderColor: 'border-orange-500',
    icon: Zap
  },
  medium: { 
    label: 'MOYENNE', 
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-900/30',
    borderColor: 'border-yellow-500',
    icon: Shield
  },
  low: { 
    label: 'BASSE', 
    color: 'text-blue-400',
    bgColor: 'bg-blue-900/30',
    borderColor: 'border-blue-500',
    icon: Globe
  }
};

// Configuration des origines
const ORIGIN_FLAGS = {
  russia: '🇷🇺',
  china: '🇨🇳',
  iran: '🇮🇷',
  axis: '⚔️',
  unknown: '❓'
};

const AlertsPanel = ({ token }) => {
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [criticalCount, setCriticalCount] = useState(0);
  const [highCount, setHighCount] = useState(0);
  const [filterPriority, setFilterPriority] = useState(null);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [expandedAlert, setExpandedAlert] = useState(null);

  useEffect(() => {
    loadAlerts();
  }, [filterPriority, showUnreadOnly]);

  const loadAlerts = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (showUnreadOnly) params.append('unread_only', 'true');
      if (filterPriority) params.append('priority', filterPriority);
      params.append('limit', '100');

      const res = await axios.get(`${API}/api/alerts?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.status === 'success') {
        setAlerts(res.data.alerts || []);
        setUnreadCount(res.data.unread_count || 0);
        setCriticalCount(res.data.critical_count || 0);
        setHighCount(res.data.high_count || 0);
      }
    } catch (err) {
      console.error('Error loading alerts:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const scanForAlerts = async () => {
    setIsScanning(true);
    addGlobalLog('ALERTS', 'Scan des mots-clés stratégiques...');
    toast.info('Scan des sources adversaires en cours...');

    try {
      const res = await axios.post(`${API}/api/alerts/scan`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.status === 'success') {
        const newCount = res.data.new_alerts || 0;
        if (newCount > 0) {
          toast.success(`${newCount} nouvelles alertes détectées !`);
          addGlobalLog('ALERTS', `${newCount} alertes créées`);
        } else {
          toast.info('Aucune nouvelle alerte détectée');
        }
        loadAlerts();
      }
    } catch (err) {
      console.error('Error scanning for alerts:', err);
      toast.error('Erreur lors du scan');
    } finally {
      setIsScanning(false);
    }
  };

  const markAsRead = async (alertId) => {
    try {
      await axios.post(`${API}/api/alerts/${alertId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadAlerts();
    } catch (err) {
      console.error('Error marking alert as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/api/alerts/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Toutes les alertes marquées comme lues');
      loadAlerts();
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const deleteAlert = async (alertId) => {
    try {
      await axios.delete(`${API}/api/alerts/${alertId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadAlerts();
    } catch (err) {
      console.error('Error deleting alert:', err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-black/60 border-2 border-orange-500/50 p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-900/50 rounded relative">
              <Bell className="w-7 h-7 text-orange-400" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
            <div>
              <h2 className="font-heading text-orange-400 text-2xl uppercase tracking-wider">
                Alertes Stratégiques
              </h2>
              <p className="font-mono text-white/80 text-sm">
                Mots-clés: France, OTAN, Défense, Énergie, Hormuz
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Stats badges */}
            {criticalCount > 0 && (
              <span className="px-3 py-1 bg-red-900/50 border border-red-500 text-red-400 font-mono text-sm rounded flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                {criticalCount} CRITIQUE{criticalCount > 1 ? 'S' : ''}
              </span>
            )}
            {highCount > 0 && (
              <span className="px-3 py-1 bg-orange-900/50 border border-orange-500 text-orange-400 font-mono text-sm rounded flex items-center gap-1">
                <Zap className="w-4 h-4" />
                {highCount} HAUTE{highCount > 1 ? 'S' : ''}
              </span>
            )}

            <button
              onClick={markAllAsRead}
              disabled={unreadCount === 0}
              className="px-3 py-2 bg-marine-slate/30 border border-marine-slate/50 text-white/90 font-mono text-sm flex items-center gap-2 hover:bg-marine-slate/50 disabled:opacity-50"
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="w-4 h-4" />
              Tout lire
            </button>
            <button
              onClick={scanForAlerts}
              disabled={isScanning}
              className="px-4 py-2 bg-orange-900/50 border border-orange-500/50 text-orange-400 font-mono text-sm flex items-center gap-2 hover:bg-orange-900/70 disabled:opacity-50"
              data-testid="scan-alerts-btn"
            >
              {isScanning ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              SCANNER
            </button>
          </div>
        </div>

        {/* Keywords info */}
        <div className="mt-4 p-3 bg-marine-cyan/10 border border-marine-cyan/30 flex items-start gap-3">
          <Target className="w-5 h-5 text-marine-cyan flex-shrink-0 mt-0.5" />
          <div className="font-mono text-sm text-white/80">
            <strong>Mots-clés surveillés:</strong> France, Rafale, SCALP, OTAN, Hormuz, embargo pétrolier, 
            frappe militaire, cyberattaque, Macron, Charles de Gaulle, FREMM, pipeline...
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-white/80 text-sm">Filtrer:</span>
        <button
          onClick={() => setShowUnreadOnly(!showUnreadOnly)}
          className={`px-3 py-1 font-mono text-sm border flex items-center gap-2 ${
            showUnreadOnly 
              ? 'bg-marine-cyan/20 border-marine-cyan text-marine-cyan' 
              : 'border-marine-slate/50 text-white/90 hover:bg-marine-slate/30'
          }`}
        >
          Non lues ({unreadCount})
        </button>
        <span className="text-white/30">|</span>
        {Object.entries(PRIORITY_CONFIG).map(([priority, config]) => (
          <button
            key={priority}
            onClick={() => setFilterPriority(filterPriority === priority ? null : priority)}
            className={`px-3 py-1 font-mono text-sm border flex items-center gap-2 ${
              filterPriority === priority 
                ? `${config.bgColor} ${config.borderColor} ${config.color}` 
                : 'border-marine-slate/50 text-white/90 hover:bg-marine-slate/30'
            }`}
          >
            <config.icon className="w-3 h-3" />
            {config.label}
          </button>
        ))}
      </div>

      {/* Alerts List */}
      {isLoading ? (
        <div className="bg-black/40 border border-marine-slate/30 p-8 text-center">
          <Loader2 className="w-12 h-12 text-orange-400 mx-auto mb-3 animate-spin" />
          <p className="font-mono text-white/80">Chargement des alertes...</p>
        </div>
      ) : alerts.length === 0 ? (
        <div className="bg-black/40 border border-marine-slate/30 p-8 text-center">
          <Bell className="w-12 h-12 text-orange-400/50 mx-auto mb-3" />
          <p className="font-mono text-white/80">
            {showUnreadOnly ? 'Aucune alerte non lue' : 'Aucune alerte détectée'}
          </p>
          <p className="font-mono text-white/40 text-sm mt-2">
            Cliquez sur "SCANNER" pour analyser les sources adversaires
          </p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {alerts.map((alert, idx) => {
            const priorityConfig = PRIORITY_CONFIG[alert.priority] || PRIORITY_CONFIG.low;
            const isExpanded = expandedAlert === idx;
            const PriorityIcon = priorityConfig.icon;

            return (
              <motion.div
                key={alert.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
                className={`p-4 border-l-4 ${priorityConfig.borderColor} ${priorityConfig.bgColor} ${
                  !alert.is_read ? 'bg-opacity-100' : 'bg-opacity-50 opacity-70'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    {/* Header */}
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <PriorityIcon className={`w-4 h-4 ${priorityConfig.color}`} />
                      <span className={`px-2 py-0.5 ${priorityConfig.bgColor} ${priorityConfig.color} font-mono text-xs rounded`}>
                        {priorityConfig.label}
                      </span>
                      <span className="text-lg">{ORIGIN_FLAGS[alert.source_origin] || '❓'}</span>
                      <span className="font-mono text-xs text-white/80">{alert.source}</span>
                      <span className="font-mono text-xs text-white/40">
                        {new Date(alert.created_at).toLocaleString('fr-FR')}
                      </span>
                      {!alert.is_read && (
                        <span className="px-2 py-0.5 bg-blue-900/50 text-blue-400 font-mono text-xs rounded">
                          NOUVEAU
                        </span>
                      )}
                    </div>

                    {/* Title */}
                    <h4 
                      className="font-mono text-sm text-white font-medium cursor-pointer hover:text-marine-cyan"
                      onClick={() => setExpandedAlert(isExpanded ? null : idx)}
                    >
                      {alert.title}
                    </h4>

                    {/* Keywords */}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {alert.keywords_matched?.map((kw, i) => (
                        <span key={i} className="px-2 py-0.5 bg-red-900/40 text-red-300 font-mono text-xs rounded">
                          {kw}
                        </span>
                      ))}
                    </div>

                    {/* Expanded content */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-3"
                        >
                          <p className="font-mono text-xs text-white/90 mb-3">
                            {alert.content}
                          </p>
                          {alert.article_url && (
                            <a
                              href={alert.article_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-marine-cyan text-xs font-mono hover:underline"
                            >
                              <ExternalLink className="w-3 h-3" />
                              Voir l'article original
                            </a>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!alert.is_read && (
                      <button
                        onClick={() => markAsRead(alert.id)}
                        className="p-1.5 text-green-400 hover:bg-green-900/30 rounded"
                        title="Marquer comme lu"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => deleteAlert(alert.id)}
                      className="p-1.5 text-red-400 hover:bg-red-900/30 rounded"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setExpandedAlert(isExpanded ? null : idx)}
                      className="p-1.5 text-white/80 hover:bg-white/10 rounded"
                    >
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AlertsPanel;
